import PropTypes from "prop-types";
import { Tabs, Tab, Box } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";

function sizeTokens(sz) {
  switch (sz) {
    case "sm":
      return { tabH: 32, padX: 10, fz: 13, sumFz: 11, radius: 9 };
    case "lg":
      return { tabH: 48, padX: 16, fz: 16, sumFz: 13, radius: 12 };
    case "md":
    default:
      return { tabH: 40, padX: 12, fz: 14, sumFz: 12, radius: 10 };
  }
}

function colorTokens(theme, variant) {
  const p = theme.palette;
  const lightFrame =
    theme.palette.mode === "light" ? "#f6f7f9" : p.background.paper;

  const pickAccent = (v) => {
    switch (v) {
      case "black":
        return "#111111";
      case "grey":
        return p.grey[700];
      case "success":
        return p.success.main;
      case "warning":
        return p.warning.main;
      case "error":
        return p.error.main;
      case "info":
        return p.info.main;
      case "blue":
      default:
        return p.primary.main;
    }
  };

  const accent = pickAccent(variant);
  return {
    frameBg: lightFrame,
    frameBorder: p.divider,
    accent,
    accentText: accent,
    accentBorder: alpha(accent, 0.35),
    accentSoftBg: alpha(accent, 0.06),
    activeBg: theme.palette.common.white,
    summaryBg: theme.palette.common.white,
  };
}

function summaryTokens(theme, pillColorTokens, summaryColor) {
  const p = theme.palette;
  if (!summaryColor) {
    return {
      text: pillColorTokens.accentText,
      border: pillColorTokens.accentBorder,
      bg: pillColorTokens.summaryBg,
      bgSelected: pillColorTokens.accentSoftBg,
      borderSelected: pillColorTokens.accent,
    };
  }
  // map "danger" | "warning" | "info" → theme palettes
  const map = {
    danger: p.error.main,
    warning: p.warning.main,
    info: p.info.main,
  };
  const base = map[summaryColor] ?? pillColorTokens.accent;
  return {
    text: base,
    border: alpha(base, 0.35),
    bg: alpha(base, 0.06),
    bgSelected: alpha(base, 0.12),
    borderSelected: base,
  };
}

const TabsFrame = styled(Box, {
  shouldForwardProp: (prop) => prop !== "pillColor" && prop !== "pillSize",
})(({ theme, pillColor, pillSize }) => {
  const c = colorTokens(theme, pillColor);
  const s = sizeTokens(pillSize);
  return {
    background: c.frameBg,
    border: `1px solid ${c.frameBorder}`,
    borderRadius: s.radius + 2,
    padding: 4,
  };
});

const StyledTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== "pillSize" && prop !== "pillColor",
})(({ theme, pillSize, pillColor }) => {
  const s = sizeTokens(pillSize);
  const c = colorTokens(theme, pillColor);
  return {
    minHeight: s.tabH + 4,
    display: "flex",
    alignItems: "center",
    "& .MuiTabs-indicator": { display: "none" },
    "& .MuiTabs-scrollButtons": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      borderRadius: 14,
      border: `1px solid ${c.accentBorder}`,
      background: theme.palette.background.paper,
      color: c.accentText,
      "&.Mui-disabled": { opacity: 0 },
    },
  };
});

const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) =>
    !["pillColor", "pillSize", "summaryColor"].includes(prop),
})(({ theme, pillColor, pillSize, summaryColor }) => {
  const c = colorTokens(theme, pillColor);
  const s = sizeTokens(pillSize);
  const b = summaryTokens(theme, c, summaryColor);

  return {
    textTransform: "none",
    minHeight: s.tabH,
    height: s.tabH,
    padding: `6px ${s.padX}px`,
    marginRight: 6,
    borderRadius: s.radius,
    lineHeight: 1.2,
    fontSize: s.fz,
    whiteSpace: "nowrap",
    color: c.accentText,
    "& .TabLabel": { display: "inline-flex", alignItems: "center", gap: 8 },
    "& .MuiSvgIcon-root": { fontSize: s.fz + 2, color: "currentColor" },

    "& .TabSummary": {
      marginLeft: 6,
      padding: "2px 6px",
      fontSize: s.sumFz,
      borderRadius: s.radius,
      border: `1px solid ${b.border}`,
      color: b.text,
      background: b.bg,
    },

    "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
      color: c.accentText,
      background: c.activeBg,
      boxShadow: `inset 0 0 0 2px ${c.accent}`,
      fontWeight: 700,
      "& .TabSummary": {
        borderColor: b.borderSelected,
        background: b.bgSelected,
        color: b.text,
      },
    },

    "&:not(.Mui-selected):hover": { background: c.accentSoftBg },

    "&.Mui-disabled": {
      color: theme.palette.action.disabled,
      "& .TabSummary": {
        borderColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
      },
    },
  };
});

export default function PillTabs({
  items = [], // [{ value, label, icon?, summary?, summaryColor?, disabled? }]
  value,
  onChange,
  ariaLabel = "tabs",
  color = "blue", // 'blue' | 'black' | 'grey' | 'success' | 'warning' | 'error' | 'info'
  size = "md", // 'sm' | 'md' | 'lg'
  sx,
  allowScrollButtonsMobile = true,
  className,
}) {
  return (
    <TabsFrame pillColor={color} pillSize={size} sx={sx} className={className}>
      <StyledTabs
        value={value}
        onChange={(_, v) => onChange?.(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile={allowScrollButtonsMobile}
        aria-label={ariaLabel}
        pillSize={size}
        pillColor={color}
      >
        {items.map((t) => (
          <StyledTab
            key={t.value}
            value={t.value}
            disabled={t.disabled}
            aria-label={t.ariaLabel}
            disableRipple
            pillColor={color}
            pillSize={size}
            summaryColor={t.summaryColor} // NEW
            label={
              <span className="TabLabel">
                {t.icon && <span className="d-inline-flex">{t.icon}</span>}
                <span>{t.label}</span>
                {!!t.summary && (
                  <span className="TabSummary">{t.summary}</span>
                )}
              </span>
            }
          />
        ))}
      </StyledTabs>
    </TabsFrame>
  );
}

PillTabs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
      summary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      summaryColor: PropTypes.oneOf(["danger", "warning", "info"]), // NEW
      disabled: PropTypes.bool,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  ariaLabel: PropTypes.string,
  color: PropTypes.oneOf([
    "blue",
    "black",
    "grey",
    "success",
    "warning",
    "error",
    "info",
  ]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  sx: PropTypes.object,
  allowScrollButtonsMobile: PropTypes.bool,
};
